///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ops dashboard API: admin task inbox + summary strip.
///

import { authenticatedApi } from './authenticatedApi'

export type OpsTaskSeverity = 'info' | 'warn' | 'urgent'
export type OpsTaskStatus = 'open' | 'snoozed' | 'done' | 'dismissed'

export interface OpsTask {
  id: string
  type: string
  severity: OpsTaskSeverity
  title: string
  body: string | null
  entity_ref: {
    kind?: string
    id?: string
    user_id?: number
    user_email?: string
  } | null
  status: OpsTaskStatus
  snoozed_until: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  resolution_note: string | null
  resolved_by_email: string | null
}

export interface OpsSummary {
  openTasks: { urgent: number; warn: number; info: number }
  openDisputes: number
  activeSubscriptions: number
  newUsers: { thisWeek: number; lastWeek: number }
}

export type ServiceHealthStatus = 'ok' | 'stale' | 'failing'

export interface ServiceHealth {
  id: string
  label: string
  kind: 'cron' | 'ingest' | 'queue' | 'webhook'
  status: ServiceHealthStatus
  last_success: string | null
  last_error: string | null
  last_error_message: string | null
  expected_interval_s: number
}

export interface AdminShare {
  slug: string
  share_type: 'view' | 'placemark' | 'dataset'
  title: string
  view_count: number
  created_at: string
  expires_at: string | null
  user_id: number
  owner_email: string
  owner_username: string | null
}

export const opsApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpsSummary: builder.query<OpsSummary, void>({
      query: () => '/api/admin/ops/summary',
      providesTags: ['OpsSummary'],
    }),

    getOpsHealth: builder.query<{ services: ServiceHealth[] }, void>({
      query: () => '/api/admin/ops/health',
      providesTags: ['OpsHealth'],
    }),

    getOpsTasks: builder.query<{
      tasks: OpsTask[]
      types: string[]
      pagination: { page: number; limit: number; total: number; pages: number }
    }, {
      status?: string
      type?: string
      page?: number
    }>({
      query: ({ status = 'open', type, page = 1 }) => {
        const params = new URLSearchParams()
        params.set('status', status)
        params.set('page', String(page))
        if (type) params.set('type', type)
        return `/api/admin/ops/tasks?${params}`
      },
      providesTags: ['OpsTasks'],
    }),

    resolveOpsTask: builder.mutation<{ success: boolean }, { id: string; note?: string }>({
      query: ({ id, note }) => ({
        url: `/api/admin/ops/tasks/${id}/resolve`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['OpsTasks', 'OpsSummary'],
    }),

    snoozeOpsTask: builder.mutation<{ success: boolean }, { id: string; days: number }>({
      query: ({ id, days }) => ({
        url: `/api/admin/ops/tasks/${id}/snooze`,
        method: 'POST',
        body: { days },
      }),
      invalidatesTags: ['OpsTasks', 'OpsSummary'],
    }),

    dismissOpsTask: builder.mutation<{ success: boolean }, { id: string; note?: string }>({
      query: ({ id, note }) => ({
        url: `/api/admin/ops/tasks/${id}/dismiss`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['OpsTasks', 'OpsSummary'],
    }),

    // Share kill switch: inspect a share before an audited takedown
    lookupShare: builder.query<{ share: AdminShare }, string>({
      query: (slug) => `/api/admin/shares/${encodeURIComponent(slug)}`,
    }),

    takedownShare: builder.mutation<{ success: boolean }, { slug: string; note?: string }>({
      query: ({ slug, note }) => ({
        url: `/api/admin/shares/${encodeURIComponent(slug)}`,
        method: 'DELETE',
        body: { note },
      }),
    }),
  }),
})

export const {
  useGetOpsSummaryQuery,
  useGetOpsHealthQuery,
  useGetOpsTasksQuery,
  useResolveOpsTaskMutation,
  useSnoozeOpsTaskMutation,
  useDismissOpsTaskMutation,
  useLazyLookupShareQuery,
  useTakedownShareMutation,
} = opsApi
